import { api } from "@jourfx/convex/client";
import type { ConvexReactClient } from "convex/react";

export type SystemBrandingPublic = {
  enabled: boolean;
  colors: {
    primaryHex: string;
    secondaryHex?: string;
    accentHex?: string;
  };
  typography: {
    fontFamily: "lato";
    scale?: "standard" | "large";
  };
  texts: {
    de: Record<string, string>;
    en: Record<string, string>;
  };
  updatedAt: number;
};

const STORAGE_KEY = "jourfx.systemBranding.v1";

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function hexToRgb(hex: string) {
  const m = hex.trim().match(/^#?([0-9a-fA-F]{6})$/);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return { r, g, b };
}

function rgbToHsl({ r, g, b }: { r: number; g: number; b: number }) {
  const r1 = r / 255;
  const g1 = g / 255;
  const b1 = b / 255;
  const max = Math.max(r1, g1, b1);
  const min = Math.min(r1, g1, b1);
  const d = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r1:
        h = ((g1 - b1) / d) % 6;
        break;
      case g1:
        h = (b1 - r1) / d + 2;
        break;
      default:
        h = (r1 - g1) / d + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hexToHslTriplet(hex: string) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb);
  const s2 = clamp(s, 10, 80);
  const l2 = clamp(l, 8, 92);
  return `${h} ${s2}% ${l2}%`;
}

function setVar(name: string, value: string | null) {
  if (value === null) document.documentElement.style.removeProperty(name);
  else document.documentElement.style.setProperty(name, value);
}

export function readCachedBranding(): SystemBrandingPublic | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SystemBrandingPublic;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.updatedAt !== "number") return null;
    if (!parsed.colors?.primaryHex) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeCachedBranding(branding: SystemBrandingPublic) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(branding));
  } catch {
    // ignore
  }
}

export function applyBrandingFromCache() {
  const cached = readCachedBranding();
  if (cached) applyBranding(cached);
}

export async function refreshBranding(convex: ConvexReactClient) {
  const remote = (await convex.query((api as any).admin.branding.getPublic, {})) as SystemBrandingPublic;
  const cached = readCachedBranding();
  if (!cached || cached.updatedAt !== remote.updatedAt) {
    writeCachedBranding(remote);
    applyBranding(remote);
  }
}

export function applyBranding(branding: SystemBrandingPublic) {
  (window as any).__jourfxBrandingUpdatedAt = branding.updatedAt;

  if (!branding.enabled) {
    // Clear inline overrides.
    for (const name of [
      "--primary",
      "--accent",
      "--ring",
      "--secondary",
      "--sidebar-primary",
      "--sidebar-ring",
    ]) {
      setVar(name, null);
    }
    document.documentElement.style.removeProperty("--font-sans");
    document.documentElement.dataset.branding = "default";

    // Restore default favicon.
    updateFavicon("/vite.svg");
    return;
  }

  const primary = hexToHslTriplet(branding.colors.primaryHex);
  const accent = hexToHslTriplet(branding.colors.accentHex || branding.colors.primaryHex);
  const secondary = branding.colors.secondaryHex ? hexToHslTriplet(branding.colors.secondaryHex) : null;

  if (primary) {
    setVar("--primary", primary);
    setVar("--sidebar-primary", primary);
  }
  if (accent) {
    setVar("--accent", accent);
  }
  if (secondary) {
    setVar("--secondary", secondary);
  }

  // Ring: use a slightly lighter version of primary if possible.
  if (primary) {
    const [hStr, sStr, lStr] = primary.split(" ");
    const l = parseFloat(lStr.replace("%", ""));
    const ring = `${hStr} ${sStr} ${clamp(l + 16, 10, 92)}%`;
    setVar("--ring", ring);
    setVar("--sidebar-ring", ring);
  }

  if (branding.typography?.fontFamily === "lato") {
    setVar("--font-sans", "'Lato', system-ui, sans-serif");
  }

  // Dynamic favicon from branding endpoint.
  const convexSiteUrl = (
    import.meta.env.VITE_CONVEX_SITE_URL ||
    import.meta.env.VITE_CONVEX_URL ||
    "http://localhost:3211"
  ).replace(/\/$/, "");
  updateFavicon(`${convexSiteUrl}/api/branding/favicon?v=${branding.updatedAt}`);

  document.documentElement.dataset.branding = "custom";
}

function updateFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

